import { getContentByUrl, getJSONByUrl } from 'widgets/toolbox/ajax';
import { isBase64Encoded } from '../utils';
import { TEmitBusEvent } from 'widgets/global/EmitBusEvent';

/**
 * @description Base DrawerDialog implementation
 * @param Modal Base widget for extending
 * @returns DrawerDialog class
 */
function DrawerDialogClassCreator(Modal: import('widgets/global/Modal').TModal) {
    /**
     * @category widgets
     * @subcategory global
     * @class DrawerDialog
     * @augments Modal
     * @classdesc Drawer Dialog component. Allows different modals to be triggered by firing an event {@link module#dialog:drawershow}.
     * <br>Was implemented to get rid from excessive duplicated popups markup in product lists etc.
     * <br>Also allow standalone components/link to trigger modal by using {@link EmitBusEvent} widget.
     * <br>In general could be triggered either programmatically by firing an event, or using {@link EmitBusEvent} widget in DOM markup.
     * <br>Allows: add classes to container; render modal heading; render modal action buttons; render content either via URL, or from string.
     * @property {string} data-widget - Widget name `DrawerDialog`
     * @property {string} data-disable-rendering - property should be set to `false` to allow rendering inside popup template.
     * @example <caption>Drawer Dialog widget markup</caption>
     * <div
     *     data-widget="drawerDialog"
     *     data-disable-rendering="false"
     *     data-accessibility-alerts='{
     *         "dialogContentLoaded": "${Resource.msg('alert.dialogContentLoaded', 'global', null)}"
     *     }'
     * >
     *     <div
     *         class="b-drawer"
     *         data-ref="container"
     *         hidden="hidden"
     *         data-label-loading="${Resource.msg('common.loading', 'common', '')}"
     *     >
     *         <div
     *             class="b-drawer-container"
     *             role="dialog"
     *             data-ref="dialog"
     *             aria-modal="true"
     *             aria-label="${Resource.msg('common.loading', 'common', '')}"
     *         >
     *             <div class="b-drawer-header">
     *                 <isinclude template="components/modal/closeButton">
     *             </div>
     *             <div
     *                 class="b-drawer-content b-user_content"
     *                 data-ref="content"
     *             ></div>
     *         </div>
     *     </div>
     *     <script type="template/mustache" data-ref="template">
     *         {{^url}}
     *             <div class="b-drawer" data-ref="container">
     *         {{/url}}
     *         <div
     *             class="b-drawer-container"
     *             role="dialog"
     *             data-ref="dialog"
     *             aria-modal="true"
     *             title="{{headerText}}"
     *             aria-label="{{ariaLabel}}"
     *         >
     *             <div class="b-drawer-header">
     *                 ... header content
     *             </div>
     *             <div class="b-drawer-content b-user_content" data-ref="content">
     *                 ... dialog content
     *             </div>
     *             <div class="b-drawer-footer m-actions">
     *                 ... action buttons content
     *             </div>
     *         </div>
     *         {{^url}}
     *             </div>
     *         {{/url}}
     *     </script>
     * </div>
     *
     * @example <caption>Drawer Dialog widget triggering via DOM</caption>
     * <button
     *     class="b-promotion-details"
     *     type="button"
     *     aria-describedby="promotions-${promotion.id}"
     *     data-widget="emitBusEvent"
     *     data-bus-event-type="drawershow"
     *     data-event-click.prevent="emitBusEvent"
     *     data-tau="promotion_details_cta"
     *     data-modal-config='{
     *         "content": "${promotion.detailsJSONSafe}",
     *         "attributes": {
     *             "data-tau": "promotion_details_dialog"
     *         },
     *         "ariaLabel": "${Resource.msg('promotion.details.label','product',null)}"
     *     }'
     * >
     *     ${Resource.msg('promotion.details','product',null)}
     * </button>
     */
    class DrawerDialog extends Modal {
        wrapperClasses?: string | Array<string>;

        onAfterCloseListener?: (DrawerDialog: this) => void;

        onCancelListener?: (DrawerDialog: this) => void;

        defaultDialogTitle = '';

        private static hasGlobalListener = false;

        private drawerType: 'menu' | 'global' = 'global';

        prefs() {
            return {
                hamburgerMenuHide: 'm-drawer_open',
                globalAlertsHide: 'm-hide_alerts',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization logic
         * @listens module:events#drawershow
         */
        init() {
            super.init();

            const attr = this.ref('container')?.get(0)?.getAttribute('data-drawer-type');

            this.drawerType = attr === 'menu' ? 'menu' : 'global';

            if (this.drawerType === 'menu') {
                this.eventBus().on('drawershow:menu', 'showModal');

                return;
            }

            if (!DrawerDialog.hasGlobalListener) {
                this.eventBus().on('drawershow', 'showModal');

                DrawerDialog.hasGlobalListener = true;
            }

            this.eventBus().on('drawer.cancel', 'cancel');
            this.eventBus().on('update.drawer.title', 'updateTitle');
        }

        /**
         * @description Shows modal. Depending on needs, content for modal could be loaded via URL, or fetched from parameters.
         * Input parameter also contains setup for modal, like container classes, action buttons, heading text etc.
         * @example <caption>An object with expected parameters</caption>
         * {
         *      showActions: true,
         *      headerText: 'Header text',
         *      actions: [{
         *          text: 'Cancel',
         *          handler: 'cancel',
         *          classes: 'm-outline'
         *      },{
         *          text: 'Confirm',
         *          handler: 'cancel',
         *          lastFocusElement: true
         *      }],
         *      wrapperClasses: 'class1 class2',
         *      ariaLabel: 'test aria label',
         *      url: 'http://www.google.com',
         *      contentType: 'json'
         * }
         * In case if input parameter is an instance of {@link EmitBusEvent},
         * modal setup properties will be taken from `data-modal-config`
         * attribute of a {@link EmitBusEvent} widget.
         * @emits DrawerDialog#show
         * @param templateData Input object for modal popup. See example above.
         * @param cb - Callback function
         * @returns Promise object represents modal rendering result
         */
        // eslint-disable-next-line sonarjs/cognitive-complexity
        showModal(templateData?: Record<string, unknown> | InstanceType<TEmitBusEvent>, cb?: () => void): Promise<void> {
            const EmitBusEventClass = <TEmitBusEvent>this.getConstructor('emitBusEvent');
            const isCalledByEmitBus = templateData instanceof this.getConstructor('emitBusEvent');
            const isMenuDrawer = this.ref('container')?.get(0)?.getAttribute('data-drawer-type') === 'menu';

            if (isMenuDrawer && !isCalledByEmitBus) {
                return Promise.resolve();
            }

            let modalData: {
                content?: string;
                url?: string;
                contentType?: string;
                wrapperClasses?: Array<string> | string;
                bodyClass?: string;
                trigger?: Node;
                onAfterClose?: (DrawerDialog: DrawerDialog) => void;
                onCancel?: (DrawerDialog: DrawerDialog) => void;
            };

            if (templateData instanceof EmitBusEventClass) {
                const url = templateData.data<string>('url');

                modalData = {
                    url: url && isBase64Encoded(url) ? atob(url) : url
                };

                const modalConfig = templateData.data<Record<string, unknown>>('modalConfig');

                if (modalConfig && Object.keys(modalConfig).length) {
                    if (modalConfig.url && isBase64Encoded(modalConfig.url as string)) {
                        modalConfig.url = atob(modalConfig.url as string);
                    }

                    if (modalConfig.urlParams && Object.keys(modalConfig.urlParams).length) {
                        const urlObj = new URL(modalConfig.url as string, window.location.origin);

                        Object.keys(modalConfig.urlParams).forEach(key => {
                            urlObj.searchParams.set(key, (modalConfig.urlParams as Record<string, string>)[key]);
                        });

                        modalConfig.url = urlObj.toString();
                    }

                    modalData = Object.assign(modalData, modalConfig);
                }
            } else {
                modalData = templateData || {};

                if (modalData.url && isBase64Encoded(modalData.url)) {
                    modalData.url = atob(modalData.url);
                }
            }

            this.onCancelListener = modalData.onCancel;
            this.onAfterCloseListener = modalData.onAfterClose;

            this.wrapperClasses = modalData.wrapperClasses;

            if (modalData.bodyClass) {
                this.classesGlobalDialog = modalData.bodyClass;
            }

            this.beforeOpenModal(modalData);

            this.ref('html').addClass(this.prefs().hamburgerMenuHide).addClass(this.prefs().globalAlertsHide);

            if (modalData.content) {
                return super
                    .showModal(modalData, cb)
                    .then(() => {
                        if (this.wrapperClasses) {
                            this.ref(this.prefs().refContainer).addClass(this.wrapperClasses);
                        }
                    })
                    .finally(() => {
                        /**
                         * @description Event dispatched, when Drawer Dialog was shown
                         * @event DrawerDialog#show
                         */
                        this.emit('show');
                    });
            }

            if (!modalData.url) {
                return Promise.resolve();
            }

            super.showModalPreview(modalData.wrapperClasses);

            if (modalData.contentType === 'json') {
                return getJSONByUrl(modalData.url, undefined, true).then(res => {
                    return this.showModalOnResponse(res, modalData, cb);
                });
            } else {
                return getContentByUrl(modalData.url).then(res => {
                    return this.showModalOnResponse(res, modalData, cb);
                });
            }
        }

        /**
         * @description process response data and pass it to modal
         * @param response - response object
         * @param modalData - modal data
         * @param cb - callback function
         * @returns Promise object represents modal rendering result
         */
        showModalOnResponse(response, modalData, cb) {
            if (Object.keys(response).length) {
                modalData = Object.assign(modalData, { content: response });
                const promise = super.showModal(modalData, cb, Boolean(modalData.url));

                /**
                 * @description Event dispatched, when Drawer Dialog was shown
                 * @event DrawerDialog#show
                 */
                this.emit('show');
                this.afterShowModal();

                return promise;
            }

            return Promise.resolve();
        }

        /**
         * @description Removes wrap classes from modal
         */
        removeWrapperClasses() {
            this.ref('html').removeClass(this.prefs().hamburgerMenuHide);

            setTimeout(() => {
                this.ref('html').removeClass(this.prefs().globalAlertsHide);
            }, 4000);

            if (this.wrapperClasses) {
                const container = this.ref(this.prefs().refContainer);

                container.removeClass(this.wrapperClasses);

                this.wrapperClasses = undefined;
            }
        }

        /**
         * @description Before open modal callback
         * @param modalData - modal data
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        beforeOpenModal(modalData: object) {
            // just for extending
        }

        /**
         * @description On after modal close callback
         */
        onAfterCloseModal() {
            this.removeWrapperClasses();

            if (this.onAfterCloseListener) {
                this.onAfterCloseListener(this);
                this.onAfterCloseListener = undefined;
            }
        }

        /**
         * @description Cancel Handler
         */
        cancel() {
            super.cancel();

            if (this.onCancelListener) {
                this.onCancelListener(this);
                this.onCancelListener = undefined;
            }
        }

        /**
         * Updates the title text of the drawer dialog if a non-empty title is provided
         *
         * @param params - The parameters object
         * @param params.title - The new title text to be set
         * @param params.default - Whether to reset the title to the default value
         */
        updateTitle({ title, default: isDefault }: { title?: string; default?: boolean }) {
            if (this.defaultDialogTitle === '') {
                this.defaultDialogTitle = this.ref('title').getText();
            }

            if (title?.length) {
                this.ref('title').setText(title);
            }

            if (isDefault) {
                this.ref('title').setText(this.defaultDialogTitle);
            }
        }
    }

    return DrawerDialog;
}

export type TDrawerDialog = ReturnType<typeof DrawerDialogClassCreator>;

export type TDrawerDialogInstance = InstanceType<TDrawerDialog>;

export default DrawerDialogClassCreator;
